import { useCallback, useEffect, useRef, useState } from 'react';
import createSpeechDetector from './speechDetector';
import createAudioQueue from './audioQueue';
import encodeWav from './encodeWav';
import parseSseStream from '../../../../utils/parseSseStream';

// De motor van de AI-interviewer in de browser.
//
// Eén beurt loopt zo: de microfoon levert blokjes geluid, de detector zegt
// wanneer de respondent klaar is, dat stuk gaat als WAV naar `aiInterviewerTurn`,
// en de gebeurtenissen die terugkomen worden meteen afgespeeld en getoond.
//
// Twee dingen zijn geen detail:
// - De microfoon blijft AAN terwijl Decca praat. Begint de respondent te
//   praten, dan zwijgt Decca binnen een paar honderd milliseconden en telt die
//   beurt niet meer mee. Zonder dat praat de app door de gebruiker heen.
// - De AudioContext wordt pas gemaakt bij de klik op 'beginnen'. Mobiel Safari
//   weigert geluid af te spelen dat niet uit een handeling van de gebruiker
//   voortkomt, en dat faalt stil.
//
// AI-CONTEXT: See docs/features/ai-interviewer-ovh-stt-tts.md

const TURN_URL = 'https://europe-west1-deccos-app.cloudfunctions.net/aiInterviewerTurn';
const FRAME_SIZE = 2048;

/**
 * @typedef {object} InterviewEvent
 * @property {string} id
 * @property {'Decca'|'Jij'} speaker
 * @property {string} transcript
 */

/**
 * @param {object} options
 * @param {string} options.languageCode - Taal uit de url
 * @param {() => Promise<object>} options.buildRequest - Levert de vaste velden van het verzoek
 * @param {(call: {callId: string, name: string, args: object}) => Promise<object|null>} options.onToolCall
 *        Voert een tool uit; geeft terug wat er naar het model terug moet, of `null` om te stoppen
 * @param {string} [options.turnUrl] - Vervangbaar voor tests
 * @returns {object}
 */
const useTurnInterviewer = ({ languageCode, buildRequest, onToolCall, turnUrl = TURN_URL }) => {
    const [events, setEvents] = useState(/** @type {InterviewEvent[]} */([]));
    const [started, setStarted] = useState(false);
    const [listening, setListening] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(/** @type {string | null} */(null));
    // Voor welke taal de server meldde dat er geen stem is. Per taal en niet als
    // vlag: wisselt de respondent naar een taal mét stem, dan moet hij die weer
    // horen, zonder de pagina te verversen.
    const [silentLanguage, setSilentLanguage] = useState(/** @type {string | null} */(null));
    const voiceAvailable = silentLanguage !== languageCode;

    const streamRef = useRef(/** @type {MediaStream | null} */(null));
    const contextRef = useRef(/** @type {AudioContext | null} */(null));
    const queueRef = useRef(null);
    const turnRef = useRef(0);
    const messagesRef = useRef([]);
    const secondsUsedRef = useRef(0);
    const eventsRef = useRef([]);
    const stoppedRef = useRef(false);

    const addEvent = useCallback((speaker, transcript) => {
        if (!transcript) return;
        const event = { id: `${Date.now()}-${Math.random()}`, speaker, transcript };
        eventsRef.current = [...eventsRef.current, event];
        setEvents(eventsRef.current);
    }, []);

    /** Stuurt één beurt en verwerkt alles wat terugkomt. */
    const sendTurn = useCallback(async ({ audio, toolResult }) => {
        if (stoppedRef.current) return;
        turnRef.current += 1;
        const turnId = turnRef.current;
        setBusy(true);

        try {
            const base = await buildRequest();
            const body = {
                ...base,
                languageCode,
                turnId,
                turnIndex: turnId - 1,
                audioSecondsUsed: secondsUsedRef.current,
                messages: messagesRef.current,
                ...(audio ? { audio } : {}),
                ...(toolResult ? { toolResult } : {}),
            };
            if (audio) secondsUsedRef.current += audio.seconds;

            const response = await fetch(turnUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (!response.ok || !response.body) throw new Error(`turn ${response.status}`);

            let gezegd = '';
            let tool = null;

            await parseSseStream(response.body, (event) => {
                // Alles van een beurt die de respondent onderbrak is oud nieuws.
                if (event.turnId && event.turnId !== turnRef.current) return;
                switch (event.type) {
                    case 'transcript':
                        addEvent('Jij', event.text);
                        messagesRef.current = [...messagesRef.current, { role: 'user', content: event.text }];
                        break;
                    case 'chunk':
                        gezegd += event.text;
                        break;
                    case 'audio':
                        queueRef.current?.push(event.turnId, event);
                        break;
                    case 'voice_unavailable':
                        setSilentLanguage(languageCode);
                        break;
                    case 'tool_call':
                        tool = { callId: event.callId, name: event.name, args: event.args };
                        break;
                    case 'error':
                        setError(event.message);
                        break;
                    default:
                        break;
                }
            });

            if (gezegd.trim()) {
                addEvent('Decca', gezegd.trim());
                messagesRef.current = [...messagesRef.current, { role: 'assistant', content: gezegd }];
            }

            if (tool) {
                messagesRef.current = [
                    ...messagesRef.current,
                    { role: 'assistant', content: gezegd, toolCalls: [{ id: tool.callId, name: tool.name, args: tool.args }] },
                ];
                const output = await onToolCall(tool);
                // `null` betekent: het interview is afgelopen, niet doorvragen.
                if (output !== null && !stoppedRef.current) {
                    await sendTurn({ toolResult: { callId: tool.callId, output: JSON.stringify(output) } });
                }
            }
        } catch (_err) {
            setError('Even geen verbinding met de assistent. Probeer het zo nog eens.');
        } finally {
            setBusy(false);
        }
    }, [addEvent, buildRequest, languageCode, onToolCall, turnUrl]);

    /** Start microfoon, detector en wachtrij. Alleen vanuit een klik. */
    const start = useCallback(async () => {
        if (started) return;
        setError(null);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
            });
            streamRef.current = stream;

            const context = new (window.AudioContext || window.webkitAudioContext)();
            // Mobiel Safari start hem geblokkeerd; dit mag alleen vanuit een klik.
            if (context.state === 'suspended') await context.resume();
            contextRef.current = context;

            queueRef.current = createAudioQueue();

            const detector = createSpeechDetector({
                sampleRate: context.sampleRate,
                onSpeechStart: () => {
                    // Praat de respondent door Decca heen? Dan zwijgt Decca.
                    if (queueRef.current?.isSpeaking()) queueRef.current.interrupt();
                },
                onSpeechEnd: (samples) => {
                    detector.pause();
                    setListening(false);
                    const wav = encodeWav(samples, context.sampleRate);
                    const bytes = new Uint8Array(wav);
                    let binary = '';
                    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
                    sendTurn({
                        audio: {
                            base64: btoa(binary),
                            mime: 'audio/wav',
                            seconds: samples.length / context.sampleRate,
                        },
                    }).finally(() => {
                        if (stoppedRef.current) return;
                        detector.resume();
                        setListening(true);
                    });
                },
            });

            const source = context.createMediaStreamSource(stream);
            const processor = context.createScriptProcessor(FRAME_SIZE, 1, 1);
            processor.onaudioprocess = (e) => detector.push(e.inputBuffer.getChannelData(0));
            source.connect(processor);
            // Naar de uitgang met volume 0: zonder verbinding naar de bestemming
            // roept de browser onaudioprocess niet aan, en dan gebeurt er niets.
            const stil = context.createGain();
            stil.gain.value = 0;
            processor.connect(stil);
            stil.connect(context.destination);

            setStarted(true);
            setListening(true);
            // De introductie: geen audio, geen tool — alleen 'begin maar'.
            await sendTurn({});
        } catch (_err) {
            setError('Ik kan de microfoon niet gebruiken. Geef toestemming in je browser en probeer het opnieuw.');
        }
    }, [sendTurn, started]);

    const stop = useCallback(() => {
        stoppedRef.current = true;
        setListening(false);
        queueRef.current?.interrupt();
        streamRef.current?.getTracks().forEach((t) => t.stop());
        contextRef.current?.close?.();
        streamRef.current = null;
        contextRef.current = null;
    }, []);

    // Microfoon uit zodra het scherm weg is; anders blijft het lampje branden.
    useEffect(() => stop, [stop]);

    return { events, eventsRef, started, listening, busy, error, voiceAvailable, start, stop };
};

export { TURN_URL };
export default useTurnInterviewer;
